export const AUTO_HIDE_DAYS_OPTIONS = [0, 3, 7, 14, 30] as const
export type AutoHideDays = (typeof AUTO_HIDE_DAYS_OPTIONS)[number]
export const DEFAULT_AUTO_HIDE_DAYS: AutoHideDays = 7

const DAY_MS = 24 * 60 * 60 * 1000

export interface PageVisibilityState {
  hiddenPageIds: string[]
  pinnedPageIds: string[]
  lastUsedAt: Record<string, number>
  autoHideDays: number
  activePageId: string | null
}

export interface ArchivedPageEntry {
  pageId: string
  name: string
  lastUsedAt: number | undefined
  isPinned: boolean
  // Genuinely archived (hidden or auto-expired) vs. merely width-overflowed — the
  // drawer's "Show tab" action only does anything for the former.
  isArchived: boolean
}

// A page is archived (not rendered as a header tab) if the user hid it explicitly,
// or if it's past the auto-hide threshold, unpinned, and not the leftmost tab. A page
// with no recorded lastUsedAt has never been backfilled/visited yet — treated as
// just-used rather than infinitely stale, so pre-existing pages don't vanish the
// moment this ships (see the 2026-07-06 design doc's first-run migration decision).
//
// Active-page and leftmost exemptions must run before the hiddenPageIds check:
// active prevents a page from vanishing from its own header, and leftmost enforces
// "slot 1 is always protected" even for a page hidden earlier while it wasn't leftmost.
export function isPageArchived(
  pageId: string,
  leftmostPageId: string | undefined,
  state: PageVisibilityState,
  now: number
): boolean {
  if (pageId === state.activePageId) return false
  if (pageId === leftmostPageId) return false
  if (state.hiddenPageIds.includes(pageId)) return true
  if (!state.autoHideDays) return false
  if (state.pinnedPageIds.includes(pageId)) return false

  const lastUsedAt = state.lastUsedAt[pageId]
  if (lastUsedAt === undefined) return false

  const daysSince = (now - lastUsedAt) / DAY_MS
  return daysSince > state.autoHideDays
}

// Most-recently-used first; entries with no recorded use sort last.
export function filterAndSortArchivedEntries(
  entries: ArchivedPageEntry[],
  searchTerm: string
): ArchivedPageEntry[] {
  const term = searchTerm.trim().toLowerCase()
  const filtered = term ? entries.filter((e) => e.name.toLowerCase().includes(term)) : entries
  return [...filtered].sort((a, b) => (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0))
}
